import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';
import { isPlaceholderHref, isTouchPointer, menuIntent } from '@theme/header-menu-intent';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {boolean} touchActivated - Whether the active item was opened by a finger.
 * @property {HTMLElement | null} touchOpenedItem - A menu item a touch tap has just opened,
 *   and whose link that same tap must therefore not follow. Cleared once the tap's click has
 *   been suppressed, so the next tap on it navigates.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.overflowMenu?.addEventListener(
      'pointerleave',
      (event) => {
        if (isTouchPointer(event.pointerType)) return;

        this.#debouncedDeactivate();
      },
      { signal }
    );

    // A tap that opened a panel must not also follow the link it tapped.
    this.addEventListener('click', this.#handleClick, { signal });

    // Touch has no "the pointer moved away", so a panel opened by a finger needs
    // somewhere else to be dismissed from: a tap outside the menu, or a scroll.
    // `composedPath` rather than `contains`, because the overflow panel lives in
    // `overflow-list`'s shadow root.
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.#state.touchActivated || !this.#state.activeItem) return;
        if (event.composedPath().includes(this)) return;

        this.#closeNow();
      },
      { signal, capture: true }
    );

    window.addEventListener(
      'scroll',
      () => {
        if (!this.#state.touchActivated || !this.#state.activeItem) return;

        this.#closeNow();
      },
      { signal, passive: true }
    );

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    touchActivated: false,
    touchOpenedItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    const intent = menuIntent({
      type: event.type,
      pointerType: /** @type {PointerEvent} */ (event).pointerType,
      hasActiveItem: Boolean(this.#state.activeItem),
    });

    switch (intent) {
      case 'open-now':
        // Synchronously, not through the 0ms debounce: a debounce still defers
        // to a later task, and the `pointerleave` the browser fires at
        // finger-up would reach `deactivate` first and cancel it.
        this.#activateHandler(event);
        break;

      case 'open-soon':
        this.#shortDebouncedActivateHandler(event);
        break;

      case 'open-delayed':
        this.#longDebouncedActivateHandler(event);
        break;
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    // Past the early return above, this activation has definitely changed which
    // item is open — so a touch one is a first tap, and its click is the one to
    // suppress. A second tap on the same item returns early and leaves the latch
    // clear, which is what lets that tap follow the link.
    const byTouch = isTouchPointer(/** @type {PointerEvent} */ (event).pointerType);

    this.#state.touchActivated = byTouch;
    this.#state.touchOpenedItem = byTouch ? item : null;

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);

    // A top-level submenu is a popover anchored to the left edge of the item that
    // opened it, so an item far enough to the right can push a wide panel past the
    // page margin. Nudge it back inside, but never further than the margin on the
    // other side — losing a little alignment beats clipping the content.
    if (isDefaultSlot && submenu) {
      submenu.style.setProperty('--submenu-shift', '0px');

      const margin = parseFloat(getComputedStyle(submenu).getPropertyValue('--page-margin')) || 0;
      const { left, right } = submenu.getBoundingClientRect();
      const overshoot = right - (window.innerWidth - margin);

      if (overshoot > 0) {
        submenu.style.setProperty('--submenu-shift', `${-Math.min(overshoot, Math.max(0, left - margin))}px`);
      }
    }
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    const intent = menuIntent({
      type: event.type,
      pointerType: /** @type {PointerEvent} */ (event).pointerType,
      hasActiveItem: Boolean(this.#state.activeItem),
    });

    // Before the cancels, not after. On touch this handler runs at finger-up,
    // and cancelling there is what swallowed the tap that was still waiting to
    // open.
    if (intent === 'ignore') return;

    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;
    // iOS leaves `:hover` stuck on whatever was tapped last, so an outside tap
    // has to be able to close past this guard or the panel wedges open.
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.#state.touchActivated = false;
    this.#state.touchOpenedItem = null;
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Close the active item at once, cancelling anything still pending.
   *
   * The dismissal routes for touch — an outside tap, a scroll — are answers to
   * a gesture that has already happened, so there is nothing to debounce and no
   * hover state left worth respecting.
   */
  #closeNow = () => {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * Stop a tap that opened a panel from also navigating away from it.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const link = target.closest('[ref="menuitem"]');

    if (!(link instanceof HTMLElement)) return;

    // An item that exists only to open its panel goes nowhere. Following it
    // pushes a history entry and jumps the page to the top — on a mouse too.
    if (isPlaceholderHref(link.getAttribute('href'))) {
      event.preventDefault();
      this.#state.touchOpenedItem = null;
      return;
    }

    // A first tap on an item that has both a panel and a destination has done
    // its job by opening the panel; navigating now would take the customer off
    // the page before they could read it. The next tap follows the link.
    if (link === this.#state.touchOpenedItem) {
      event.preventDefault();
      this.#state.touchOpenedItem = null;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
